import React, { useMemo, useContext, useCallback, useState, useRef, useEffect } from 'react';
import { css } from '@emotion/react';
import { Grid, TextField } from '@material-ui/core';
import { VariableSizeList } from 'react-window';
import AutoSizer from 'react-virtualized-auto-sizer';
import { v4 as uuid } from 'uuid';
import { asDateInTZ } from '@/utilities/convertToISO';
import RouteListItem from './RouteListItem';
import { integerFormatter } from '@/constants/formats';
import { useItemQuantity, useTotalCubes } from '@/hooks';
import { MAX_ROUTES, TEMP_ROUTE } from '../constants';
import { PlanningDataContext, PlanningSelectionContext, PlanningUiContext } from '../context';
import { usePreviewOrders } from '../hooks';
import { useClientUser } from '@/hooks/useClientUser';
import { QUERY_PLANNING_DATA } from '../graphql/queries';
import { useMutation } from '@apollo/client';
import { UPDATE_ROUTE_STOPS, INSERT_MULTIPLE_ROUTES } from '../graphql/mutations';
import handleGeocode from '@/utilities/handleGeocode';
import FTLStopHelpers from '@/utilities/FTLStopHelpers';
import { Body1, Body2, PrimaryButtonFullW } from '../blocks';
import { ROUTE_COLORS } from '../constants';
import { MODALS } from '../constants';
import { routeIsMissingTimeframes, routeNeedsRecalculating } from '../util';
import { routeName } from '@/utilities/routeNames';
import { PLAN_PAGE_SHOW_RATES } from '@/constants/featureFlags';

const EMPTY_ARRAY = [];

// One virtualized row. The redesigned card is genuinely variable height (the
// revenue band is conditional, the timeframe can wrap to two lines, and the MUI
// header sizes itself), so instead of predicting the height we measure the real
// DOM node and report it back through data.setSize, which feeds getItemSize. The
// outer div keeps react-window's absolute positioning; the inner ref wraps the
// card so we read its natural height.
const RouteRow = ({ index, style, data }) => {
    const { routes, selectedRoute, previewOrders, subscribeToMore, setSelectedRoute, setSize } = data;
    const ref = useRef(null);
    const route = index <= routes.length - 1 ? routes[index] : null;
    const routeId = route?.route_id;

    useEffect(() => {
        const el = ref.current;
        if (!el || !routeId) return;
        const measure = () => setSize(routeId, index, el.getBoundingClientRect().height);
        measure();
        if (typeof ResizeObserver === 'undefined') return;
        const observer = new ResizeObserver(measure);
        observer.observe(el);
        return () => observer.disconnect();
    }, [routeId, index, setSize]);

    // Trailing spacer row (itemCount is routes.length + 1).
    if (!route) return <div style={style} />;

    return (
        <div style={style}>
            <div ref={ref}>
                <RouteListItem
                    routeIndex={index}
                    route={route}
                    subscribeToMore={subscribeToMore}
                    isSelected={route.route_id === selectedRoute}
                    previewOrders={route.route_id === selectedRoute ? previewOrders : EMPTY_ARRAY}
                    setSelectedRoute={setSelectedRoute}
                />
            </div>
        </div>
    );
};

const PlanningRoutes = () => {
    const { selectedRoute, setSelectedRoute } = useContext(PlanningSelectionContext);
    const { setError, setModalOpen, setNotification } = useContext(PlanningUiContext);
    const {
        deliveryDate,
        state: { orders, routes, variables, selectedDrivers },
        callbacks: { subscribeToMore, setRoutesToSubmitInternal, setLockAllRoutesClicked },
    } = useContext(PlanningDataContext);
    const {
        user_id,
        default_end_location,
        drivers,
        preferences_next_day_returns,
        preferences_bypass_optimize,
        preferences_require_staging_verification,
        circles,
    } = useClientUser();

    const [numRoutes, setNumRoutes] = useState(1);

    const routesToDisplay = useMemo(() => {
        if (Object.values(selectedDrivers).some((x) => x)) {
            return routes.filter((r) => !!selectedDrivers[r.driver_id]);
        }
        return routes;
    }, [drivers, routes, selectedDrivers]);

    const showLockAllRoutesButton = useMemo(() => {
        return circles?.['lock-all-routes'] === true;
    }, [circles]);

    const [addRoute, { loading: addRouteLoading }] = useMutation(INSERT_MULTIPLE_ROUTES, {
        update: (cache, { data: { insert_routes } }) => {
            const routeValues = insert_routes.returning;
            const newRoutes = Array.isArray(routeValues) ? routeValues : [routeValues];
            const tempRoutes = newRoutes.filter((route) => !isFinite(route?.route_number));
            const isTemp = tempRoutes.length > 0;

            cache.updateQuery(
                {
                    query: QUERY_PLANNING_DATA,
                    variables,
                },
                // Returning undefined skips the write when the cached query is
                // unreadable (updateQuery passes null); the pending refetch
                // paints the new routes instead.
                (data) =>
                    data?.routes
                        ? {
                              ...data,
                              routes: [
                                  ...data.routes.filter((route) => isFinite(route.route_number) || isTemp),
                                  ...newRoutes,
                              ],
                          }
                        : undefined
            );
        },
    });

    const [updateStops, { loading: stopsLoading }] = useMutation(UPDATE_ROUTE_STOPS);

    const linkedOrders = useMemo(() => {
        return routes.reduce((acc, route) => [...acc, ...route.orders.map((mapping) => mapping.order)], []);
    }, [routes]);

    // Calculate total items and total dropoffs for the routes.
    const totalItems = useItemQuantity(linkedOrders);
    const totalVolume = useTotalCubes(linkedOrders);
    const totalDropoffs = useMemo(() => {
        return routes.reduce((acc, route) => {
            return (
                acc + (route?.stopsByRouteId?.filter((stop) => FTLStopHelpers.isCustomerStop(stop, route))?.length || 0)
            );
        }, 0);
    }, [routes]);

    const getEndAddressInfo = async (address, route) => {
        const addressInfo = await handleGeocode({
            address: `${address.business_address}, ${address.business_city}, ${address.business_state}, ${address.business_zip}`,
        });
        if (addressInfo.lat && addressInfo.lng) {
            const newStops = FTLStopHelpers.addEnd(route, addressInfo);
            updateStops({
                variables: {
                    route_id: route.route_id,
                    route_update: { need_to_optimize: true },
                    ...FTLStopHelpers.gqlStopUpdates(newStops, route),
                },
                onError: (error) => {
                    setError(error, 'Error setting route default end location');
                },
            });
        }
    };

    const addRouteHandler = async () => {
        const displayedRouteColors = new Set(routesToDisplay.map((route) => route.route_color));
        const allowedRouteColors = new Set(ROUTE_COLORS);

        // pick colors not already assigned
        const availableColors = [
            ...new Set([...allowedRouteColors].filter((color) => !displayedRouteColors.has(color))),
        ];

        const newRoutes = Array.from({ length: numRoutes }, (_, i) => {
            return {
                shipper_id: user_id,
                scheduled_delivery: asDateInTZ(deliveryDate).toISOString(),
                route_color: availableColors[i % availableColors.length],
                finish_returns_next_day: preferences_next_day_returns,
                status: 'planning',
                source_form: 'PLANNING',
                need_to_optimize: true,
                planning: true,
                needs_verification: preferences_require_staging_verification,
            };
        });

        const routesObjects = newRoutes.map((r) => {
            return {
                ...r,
                ...(default_end_location?.lat && default_end_location?.lng
                    ? {
                          stopsByRouteId: {
                              data: [
                                  {
                                      ordering: 0,
                                      orders: null,
                                      returns: null,
                                      exchanges: null,
                                      type: 'DROPOFF',
                                      lat: default_end_location.lat,
                                      lng: default_end_location.lng,
                                      address: `${default_end_location.business_address}, ${default_end_location.business_city}, ${default_end_location.business_state}, ${default_end_location.business_zip}`,
                                      start: false,
                                      end: true,
                                  },
                              ],
                          },
                      }
                    : {}),
            };
        });

        setSelectedRoute(null);

        addRoute({
            optimisticResponse: {
                insert_routes: {
                    returning: routesObjects.map((routeInsert) => {
                        // Own id per optimistic route: a shared TEMP_ROUTE id
                        // normalizes them all into one cache entity.
                        return {
                            ...TEMP_ROUTE,
                            route_id: uuid(),
                            ...routeInsert,
                            stopsByRouteId: routeInsert?.stopsByRouteId?.data?.length
                                ? [...routeInsert.stopsByRouteId.data]
                                : [],
                            orders: [],
                            resource: null,
                            __typename: 'routes',
                        };
                    }),
                    __typename: 'routes_mutation_response',
                },
            },
            variables: { routes: routesObjects },
            onError: (error) => {
                setError(error, 'Error adding new route');
            },
        });
    };

    const lockAllRoutes = () => {
        const pendingRoutes = routesToDisplay.filter((r) => !r.shell && r.status === 'planning');

        const missingTimeframes = pendingRoutes.filter((r) => routeIsMissingTimeframes(r));
        if (missingTimeframes.length > 0) {
            setNotification({
                severity: 'warning',
                message: `Route${missingTimeframes.length > 1 ? 's' : ''} ${missingTimeframes
                    .map((r) => routeName(r) || r.route_number)
                    .join(', ')} ${
                    missingTimeframes.length > 1 ? 'are' : 'is'
                } missing time frames. Please click "Map Route" or "Optimize" to add them.`,
            });
            return;
        }

        const needsRecalc = pendingRoutes.filter((r) => routeNeedsRecalculating(r));
        if (needsRecalc.length > 0) {
            setNotification({
                severity: 'warning',
                message: `Stops have been rearranged on route${needsRecalc.length > 1 ? 's' : ''} ${needsRecalc
                    .map((r) => routeName(r) || r.route_number)
                    .join(', ')}. Please click "Map Route" or "Optimize" to recalculate timeframes.`,
            });
            return;
        }

        const needsOptimize = pendingRoutes.filter((r) => r.need_to_optimize && !preferences_bypass_optimize);
        if (needsOptimize.length > 0) {
            setNotification({
                severity: 'warning',
                message: `Changes have been made to route${needsOptimize.length > 1 ? 's' : ''} ${needsOptimize
                    .map((r) => routeName(r) || r.route_number)
                    .join(', ')} that require${
                    needsOptimize.length > 1 ? '' : 's'
                } timeframes to be recalculated. Please click "Map Route" or "Optimize" to recalculate timeframes.`,
            });
            return;
        }

        setRoutesToSubmitInternal(pendingRoutes);
        setLockAllRoutesClicked(true);
        setModalOpen(MODALS.ROUTE_INTERNAL);
    };

    // The live preview only ever applies to the selected route, so compute it
    // once here instead of inside every row.
    const selectedRouteObj = useMemo(
        () => routesToDisplay.find((r) => r.route_id === selectedRoute),
        [routesToDisplay, selectedRoute]
    );
    const previewOrders = usePreviewOrders(selectedRouteObj);

    const listRef = useRef(null);
    // route_id -> last measured pixel height. Keyed by id (not index) so a
    // cached height follows its route across reorders. RouteRow writes here via
    // setSize; getItemSize reads it.
    const sizeMap = useRef({});

    const setSize = useCallback((routeId, index, height) => {
        const next = Math.ceil(height);
        if (!next || sizeMap.current[routeId] === next) return;
        sizeMap.current[routeId] = next;
        // Re-lay-out this row and everything below it with the measured height.
        listRef.current?.resetAfterIndex(index);
    }, []);

    // First-paint estimate only. Real heights come from measuring the rendered
    // card (RouteRow -> setSize), so correctness never depends on this being
    // exact — it just keeps the initial scrollbar/offsets close before the first
    // measurement lands. Bumps for the revenue band a route with revenue shows.
    const estimateSize = useCallback(
        (route) => {
            if (!route) return 240;
            let carrierRevenue = 0;
            let shipperRevenue = 0;
            (route.orders || []).forEach((mapping) => {
                const order = mapping.order;
                if (order?.oms) {
                    carrierRevenue += order.order_revenue || 0;
                } else {
                    shipperRevenue += order.shipper_rate || 0;
                    carrierRevenue += order.carrier_rate || 0;
                }
            });
            if (!circles?.[PLAN_PAGE_SHOW_RATES]) shipperRevenue = 0;
            const hasRevenueRow = shipperRevenue > 0 || carrierRevenue > 0;
            return hasRevenueRow ? 310 : 260;
        },
        [circles]
    );

    const getItemSize = useCallback(
        (index) => {
            const route = routesToDisplay[index];
            const measured = route ? sizeMap.current[route.route_id] : undefined;
            return measured ?? estimateSize(route);
        },
        [routesToDisplay, estimateSize]
    );

    // Everything the row renderer needs rides on itemData so the renderer
    // itself keeps a stable identity — react-window remounts every visible row
    // when its children component type changes.
    const itemData = useMemo(
        () => ({
            routes: routesToDisplay,
            selectedRoute,
            previewOrders,
            subscribeToMore,
            setSelectedRoute,
            setSize,
        }),
        [routesToDisplay, selectedRoute, previewOrders, subscribeToMore, setSelectedRoute, setSize]
    );

    // Row count/order changed: recompute offsets from the top. Per-row height
    // changes (revenue band appearing, timeframe wrapping, async data arriving)
    // are handled by measurement in RouteRow -> setSize, not here.
    useEffect(() => {
        listRef.current?.resetAfterIndex(0);
    }, [routesToDisplay]);

    return (
        <>
            <Grid
                container
                justifyContent="space-between"
                alignItems="center"
                css={css`
                    padding: 1rem 1.5rem 1rem 1rem;
                    border-bottom: 1px solid #e2e2e2;
                    border-top: 1px solid #e2e2e2;
                `}
                onClick={() => setSelectedRoute(null)}
            >
                <Grid item>
                    <Body1>{`${routes.length} Route${routes.length !== 1 ? 's' : ''}`}</Body1>
                </Grid>
                <Grid item>
                    <Body2>
                        {[
                            `${integerFormatter.format(totalItems)} items`,
                            `${integerFormatter.format(totalVolume)} cu ft.`,
                        ].join(' / ')}
                    </Body2>
                </Grid>
                <Grid item>
                    <Body2>{`${totalDropoffs} stop${totalDropoffs !== 1 ? 's' : ''}`}</Body2>
                </Grid>
            </Grid>
            <Grid
                container
                css={css`
                    display: flex;
                    flex-grow: 1;
                    flex-basis: 0;
                `}
            >
                <AutoSizer>
                    {({ height, width }) => {
                        return (
                            <VariableSizeList
                                ref={listRef}
                                overscanCount={20}
                                height={height}
                                itemData={itemData}
                                itemCount={routesToDisplay.length + 1}
                                itemSize={getItemSize}
                                width={width}
                            >
                                {RouteRow}
                            </VariableSizeList>
                        );
                    }}
                </AutoSizer>
            </Grid>

            <Grid
                container
                direction="row"
                css={css`
                    flex-wrap: nowrap;
                    padding: 1rem;
                    align-items: center;
                `}
            >
                <Grid
                    item
                    css={css`
                        width: 60px;
                        margin-right: 1rem;
                    `}
                >
                    <TextField
                        label="Qty"
                        type="number"
                        variant="outlined"
                        value={numRoutes || 1}
                        onChange={(e) => setNumRoutes(e.target.value)}
                        size="small"
                        fullWidth
                    />
                </Grid>
                <Grid
                    item
                    css={css`
                        flex-grow: 1;
                    `}
                >
                    <PrimaryButtonFullW
                        onClick={addRouteHandler}
                        disabled={routes.length >= MAX_ROUTES || addRouteLoading}
                    >
                        Add Routes
                    </PrimaryButtonFullW>
                </Grid>
                {showLockAllRoutesButton ? (
                    <Grid
                        item
                        css={css`
                            flex-grow: 1;
                            margin-left: 1rem;
                        `}
                    >
                        <PrimaryButtonFullW onClick={lockAllRoutes}>Lock All Routes</PrimaryButtonFullW>
                    </Grid>
                ) : null}
            </Grid>
        </>
    );
};

export default PlanningRoutes;
